'use client';

import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Popover,
    Portal,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { LuBell } from 'react-icons/lu';
import { chromeSegmentStyles } from './sidebar';
import { toaster } from './toaster';

// Notifications bell for the top bar: the user's pending organization
// invites with accept/reject. Reintroduction of the standalone console's
// InvitesPopover (by way of the shell's mock NotificationsMenu), now
// presentational and shared — data, mutations and navigation stay in each
// app's container; the toast helpers below keep the feedback copy identical
// in both apps.

/** A pending org invite row — each app's invites.list output satisfies it. */
export type NotificationsMenuInvite = {
    id: string;
    organizationId: string;
    organizationName: string | null;
    organizationLogoUrl: string | null;
    inviterName: string | null;
    createdAt: Date | null;
};

export type NotificationsMenuProps = {
    /** undefined = still loading. */
    invites: NotificationsMenuInvite[] | undefined;
    /** The list query failed; renders the error panel. */
    error?: boolean;
    /** An accept/decline is in flight: every action button disables (the
     * per-action pending flags flip too late to stop a double-submit). */
    busy?: boolean;
    onAccept: (invite: NotificationsMenuInvite) => void;
    onDecline: (invite: NotificationsMenuInvite) => void;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Mirrors the standalone's invite timestamps (humanize-duration + date-fns
// there) with Intl only, so this package adds no date deps.
function formatRelativeTime(date: Date): string {
    const elapsedMs = Date.now() - date.getTime();
    if (elapsedMs < MINUTE_MS) return 'A few seconds ago';
    const relative = new Intl.RelativeTimeFormat('en');
    if (elapsedMs < HOUR_MS) {
        return relative.format(-Math.round(elapsedMs / MINUTE_MS), 'minute');
    }
    if (elapsedMs < DAY_MS) {
        return relative.format(-Math.round(elapsedMs / HOUR_MS), 'hour');
    }
    return new Intl.DateTimeFormat('en', {
        month: 'long',
        day: 'numeric',
    }).format(date);
}

export function NotificationsMenu({
    invites,
    error = false,
    busy = false,
    onAccept,
    onDecline,
}: NotificationsMenuProps) {
    const pendingCount = invites?.length ?? 0;

    return (
        <Popover.Root
            lazyMount
            autoFocus={false}
            positioning={{ placement: 'bottom-end' }}
        >
            {/* A flat cell of the top bar (chromeSegmentStyles), the chrome
                icon controls' muted ink brightening under the pointer. */}
            <Box position='relative' alignSelf='stretch' display='flex'>
                <Popover.Trigger asChild>
                    <IconButton
                        aria-label={
                            pendingCount > 0
                                ? `Notifications (${pendingCount} new)`
                                : 'Notifications'
                        }
                        variant='ghost'
                        color='{colors.fg.muted}'
                        {...chromeSegmentStyles}
                        w={14}
                        _hover={{ ...chromeSegmentStyles._hover, color: 'fg' }}
                        _expanded={{
                            ...chromeSegmentStyles._expanded,
                            color: 'fg',
                        }}
                    >
                        <Icon as={LuBell} boxSize={4} />
                    </IconButton>
                </Popover.Trigger>
                {pendingCount > 0 && (
                    <Box
                        position='absolute'
                        // Over the glyph's top-right shoulder.
                        top='calc(50% - 12px)'
                        right='calc(50% - 12px)'
                        boxSize='6px'
                        bg='red.400'
                        rounded='full'
                        zIndex={1}
                        pointerEvents='none'
                    />
                )}
            </Box>
            <Portal>
                <Popover.Positioner>
                    {/* Width via --popover-size: a plain `w` would override the
                        theme's min(100dvw - 1rem, size) guard on small screens. */}
                    <Popover.Content css={{ '--popover-size': 'sizes.sm' }}>
                        <Popover.Header
                            px={4}
                            py={3}
                            borderBottomWidth='1px'
                            borderColor='border'
                        >
                            <HStack gap={2}>
                                <Popover.Title asChild>
                                    <Text fontWeight='medium'>
                                        Notifications
                                    </Text>
                                </Popover.Title>
                                {/* Count chip instead of a sentence — ties to
                                    the trigger's unread dot. */}
                                {pendingCount > 0 && (
                                    <Badge
                                        variant='subtle'
                                        size='xs'
                                        colorPalette='red'
                                        rounded='full'
                                    >
                                        {pendingCount}
                                    </Badge>
                                )}
                            </HStack>
                        </Popover.Header>
                        {/* Same horizontal gutter as the header, and the rows
                            add none of their own, so avatars and card edges
                            align with the title. */}
                        <Popover.Body
                            px={4}
                            py={2}
                            maxH='min(60vh, 540px)'
                            overflowY='auto'
                        >
                            <NotificationsBody
                                invites={invites}
                                error={error}
                                busy={busy}
                                onAccept={onAccept}
                                onDecline={onDecline}
                            />
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
}

// Empty/error read as stock v3 alerts, no color overrides — the same
// treatment as the switcher popover's empty state, so both modes get the
// recipe's designed contrast instead of a hand-rolled subtle box.
function NotificationsBody({
    invites,
    error,
    busy,
    onAccept,
    onDecline,
}: Required<Pick<NotificationsMenuProps, 'error' | 'busy'>> &
    Pick<NotificationsMenuProps, 'invites' | 'onAccept' | 'onDecline'>) {
    if (error) {
        return (
            // Chakra's Alert.Root has no implicit role; this one appears
            // asynchronously, so announce it.
            <Alert.Root status='error' role='alert'>
                <Alert.Indicator />
                <Alert.Description>
                    Could not fetch invites, please try again later.
                </Alert.Description>
            </Alert.Root>
        );
    }
    if (invites === undefined) {
        return (
            <Flex justify='center' py={4}>
                <Spinner size='sm' color='{colors.fg.muted}' />
            </Flex>
        );
    }
    if (invites.length === 0) {
        return (
            <Alert.Root status='info'>
                <Alert.Indicator />
                <Alert.Description>
                    No notifications available
                </Alert.Description>
            </Alert.Root>
        );
    }
    return (
        <VStack align='stretch' gap={1}>
            {invites.map((invite) => (
                <InviteRow
                    key={invite.id}
                    invite={invite}
                    busy={busy}
                    onAccept={onAccept}
                    onDecline={onDecline}
                />
            ))}
        </VStack>
    );
}

function InviteRow({
    invite,
    busy,
    onAccept,
    onDecline,
}: {
    invite: NotificationsMenuInvite;
    busy: boolean;
    onAccept: (invite: NotificationsMenuInvite) => void;
    onDecline: (invite: NotificationsMenuInvite) => void;
}) {
    const organizationLabel = invite.organizationName || 'an organization';
    const titlePrefix = invite.createdAt
        ? `${formatRelativeTime(invite.createdAt)} • `
        : '';

    return (
        <Flex gap={2} py={1} alignItems='flex-start'>
            <Avatar.Root size='sm' shape='rounded'>
                {invite.organizationLogoUrl && (
                    <Avatar.Image src={invite.organizationLogoUrl} alt='' />
                )}
                <Avatar.Fallback data-sentry-mask>
                    {(
                        invite.organizationName ||
                        invite.organizationId ||
                        '?'
                    ).substring(0, 2)}
                </Avatar.Fallback>
            </Avatar.Root>
            <VStack gap={1} align='stretch' flex='1' minW={0}>
                <Text fontSize='xs' fontWeight='medium' truncate>
                    {`${titlePrefix}Organization Invite`}
                </Text>
                {/* Neutral surface alert on the CANVAS token: darker than the
                    popover panel in dark mode (near-black card), white with
                    the surface border in light — inverted contrast beats the
                    lighter-gray fill that washed out on the dark panel. */}
                <Alert.Root
                    status='neutral'
                    variant='surface'
                    bg='bg'
                    fontSize='xs'
                    p={2}
                >
                    <Alert.Content gap={2}>
                        <Alert.Description data-sentry-mask>
                            {invite.inviterName || 'A team member'} has invited
                            you to join {organizationLabel}.
                        </Alert.Description>
                        <HStack gap={2}>
                            <Button
                                flex='1'
                                size='xs'
                                variant='brand'
                                disabled={busy}
                                onClick={() => onAccept(invite)}
                            >
                                Accept invite
                            </Button>
                            <Button
                                flex='1'
                                size='xs'
                                variant='outline'
                                colorPalette='red'
                                disabled={busy}
                                onClick={() => onDecline(invite)}
                            >
                                Reject invite
                            </Button>
                        </HStack>
                    </Alert.Content>
                </Alert.Root>
            </VStack>
        </Flex>
    );
}

/** Accept feedback, identical in every app: the success toast with the
 * switch-to-the-new-org action (the caller supplies the app's own switcher
 * navigation mechanics — and must have refreshed the switcher data first,
 * so the target org exists when the action fires). */
export function showInviteAcceptedToast({
    organizationName,
    onGoToOrg,
}: {
    organizationName: string | null;
    onGoToOrg: () => void;
}) {
    toaster.create({
        type: 'success',
        title: 'Invite accepted',
        description: organizationName ? (
            <>
                You've joined{' '}
                <Text as='span' fontWeight='semibold'>
                    {organizationName}
                </Text>{' '}
                organization.
            </>
        ) : (
            "You've joined an organization."
        ),
        action: { label: 'Switch', onClick: onGoToOrg },
    });
}

/** Decline feedback, identical in every app. */
export function showInviteDeclinedToast() {
    toaster.create({
        type: 'info',
        title: 'Invite rejected',
    });
}
